import React, { type ComponentProps } from 'react';
// eslint-disable-next-line import/no-namespace
import * as DialogPrimitive from '@radix-ui/react-dialog';

import { cn } from '@design-system/helpers';
import CloseSquare from '@shared/assets/icons/shared/close-square';
import { Button } from '../button';
import { useDialogContextProvider } from './use-dialog-context-provider';
import { DialogContext } from './dialog-context';
import { useDialogContext } from './use-dialog-context';
import { Warning2 } from 'iconsax-react';

// Flickering issue is caused by data-[state=open]:animate-in.
// This is a known issue with the Radix UI Dialog component.
// https://github.com/shadcn-ui/ui/issues/1351

const DIALOG_BORDER_RADIUS = '12px';

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const Dialog = ({
  open: externalOpen,
  onOpenChange,
  ...props
}: ComponentProps<typeof DialogPrimitive.Root>) => {
  const dialogContextValue = useDialogContextProvider({ open: externalOpen });

  const {
    setShowParentDialog,
    showParentDialog,
    setShowConfirmDialog,
    showConfirmDialog,
  } = dialogContextValue;

  const isControlled = typeof externalOpen !== 'undefined';

  const effectiveOpenState = isControlled
    ? externalOpen && showParentDialog
    : showParentDialog;

  const handleOpenChange = (openState: boolean) => {
    setShowParentDialog(openState);
    onOpenChange?.(openState);
  };

  const handleConfirmationClose = () => {
    setShowConfirmDialog(false);
    setShowParentDialog(false);
    onOpenChange?.(false);
  };

  return (
    <DialogContext.Provider value={dialogContextValue}>
      <DialogPrimitive.Root
        open={effectiveOpenState}
        onOpenChange={handleOpenChange}
        {...props}
      />

      <DialogCloseConfirmation
        open={showConfirmDialog}
        onOpenChange={setShowConfirmDialog}
        onCloseConfirmDialog={handleConfirmationClose}
      />
    </DialogContext.Provider>
  );
};
Dialog.displayName = DialogPrimitive.Dialog.displayName;

const DialogClose = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Close>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Close>
>(({ className, onClick, ...props }, ref) => {
  const { openConfirmDialogIfDirty } = useDialogContext();

  return (
    <DialogPrimitive.Close
      onClick={(e) => {
        openConfirmDialogIfDirty(e);
        onClick?.(e);
      }}
      ref={ref}
      className={cn(className)}
      {...props}
    />
  );
});
DialogClose.displayName = DialogPrimitive.Close.displayName;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    onClick={(e) => e.stopPropagation()}
    className={cn(
      'fixed inset-0 z-50 bg-black/80 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0',
      className
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

type DialogAlign = 'center' | 'fullCenter' | 'right' | 'left';

/** `drawer` (default): left/right slide in from the edge. `zoom`: legacy fade + scale. */
type DialogAnimation = 'drawer' | 'zoom';

const dialogPositionClasses: Record<DialogAlign, string> = {
  center: 'left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
  fullCenter: 'left-1/2 inset-y-0 -translate-x-1/2 m-6',
  right: 'right-0 inset-y-0 m-6 h-[calc(100%-3rem)]',
  left: 'left-0 inset-y-0 m-6 h-[calc(100%-3rem)]',
};

const dialogZoomAnimationClasses =
  'duration-200 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95';

const dialogDrawerAnimationClasses: Record<'left' | 'right', string> = {
  right:
    'ease-in-out data-[state=closed]:duration-200 data-[state=open]:duration-500 data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right',
  left: 'ease-in-out data-[state=closed]:duration-200 data-[state=open]:duration-500 data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left',
};

function getDialogAnimationClasses(
  align: DialogAlign,
  animation: DialogAnimation
) {
  if (align === 'center' || align === 'fullCenter' || animation === 'zoom') {
    return dialogZoomAnimationClasses;
  }

  return dialogDrawerAnimationClasses[align];
}

const DialogContent = ({
  className,
  children,
  align = 'right',
  animation = 'drawer',
  dialogOverlayProps,
  onPointerDownOutside,
  onEscapeKeyDown,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  align?: DialogAlign;
  animation?: DialogAnimation;
  dialogOverlayProps?: React.ComponentProps<typeof DialogOverlay>;
}) => {
  const { openConfirmDialogIfDirty } = useDialogContext();

  return (
    <DialogPortal>
      <DialogOverlay {...dialogOverlayProps} />
      <DialogPrimitive.Content
        onPointerDownOutside={(e) => {
          openConfirmDialogIfDirty(e);
          onPointerDownOutside?.(e);
        }}
        onEscapeKeyDown={(e) => {
          openConfirmDialogIfDirty(e);
          onEscapeKeyDown?.(e);
        }}
        className={cn(
          'fixed z-50 flex flex-col rounded-(--dialog-border-radius) w-full max-w-lg border bg-white shadow-lg sm:rounded-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          dialogPositionClasses[align],
          getDialogAnimationClasses(align, animation),
          className
        )}
        {...props}
      >
        {children}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
};

const DialogBody = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('h-full p-6 overflow-auto', className)}
    {...props}
  />
));

const DialogHeader = ({
  onClose,
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { onClose?: () => void }) => (
  <div
    className={cn(
      ' p-6 font-medium w-full rounded-xl flex text-gray-900 items-start self-start justify-between bg-primary-30 sm:text-left',
      className
    )}
    {...props}
  >
    <div className='flex flex-col w-full'>{children}</div>
    <DialogClose
      onClick={onClose}
      tabIndex={-1}
      className='rounded-sm opacity-70  text-gray-900 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-gray-900'
    >
      <CloseSquare />
      <span className='sr-only'>Close</span>
    </DialogClose>
  </div>
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex w-full gap-2 flex-col-reverse p-6 self-end sm:flex-row sm:justify-end',
      className
    )}
    {...props}
  />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      'text-lg font-semibold leading-none tracking-tight',
      className
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

const DialogCloseConfirmation = ({
  onCloseConfirmDialog,
  ...props
}: ComponentProps<typeof DialogPrimitive.Root> & {
  onCloseConfirmDialog?: () => void;
}) => {
  const { setShowConfirmDialog, formikRef } = useDialogContext();

  if (!formikRef.current?.dirty) return null;

  return (
    <DialogPrimitive.Root
      modal
      {...props}
    >
      <DialogPortal>
        <DialogOverlay />
        <DialogPrimitive.Content
          className={cn(
            'fixed z-50 flex flex-col p-4  rounded-(--dialog-border-radius) w-full max-w-lg border bg-white shadow-lg duration-200 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 sm:rounded-lg',
            'left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2'
          )}
        >
          <div className='flex flex-col items-center gap-2'>
            <div className='bg-red-50 mb-2 flex items-center justify-center rounded-lg size-10'>
              <Warning2 className='text-destructive' />
            </div>

            <div className='flex flex-col gap-2 mb-4 text-center'>
              <DialogTitle className='text-gray-900 font-medium!'>
                Unsaved Changes
              </DialogTitle>
              <DialogDescription className='text-gray-600'>
                You have unsaved changes. Are you sure you want to close the
                form? All unsaved changes will be lost.
              </DialogDescription>
            </div>

            <div className='flex gap-2'>
              <Button
                variant='primary'
                size='sm'
                className='rounded-lg gap-1 [&_svg]:size-4'
                onClick={() => setShowConfirmDialog(false)}
              >
                Back
              </Button>
              <Button
                size='sm'
                className='rounded-lg gap-1 [&_svg]:size-4'
                variant='dangerPale'
                onClick={onCloseConfirmDialog}
              >
                Close
              </Button>
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </DialogPrimitive.Root>
  );
};

// ############################
type TDialogContext = {
  subModalValue: string | null;
  setSubModalValue: (value: string | null) => void;
};

const SubDialogContext = React.createContext<TDialogContext | null>(null);

// eslint-disable-next-line react-refresh/only-export-components
export function useSubDialog() {
  const context = React.useContext(SubDialogContext);
  if (!context) {
    throw new Error('useSubDialog must be used within a <DialogSub/>.');
  }

  return context;
}

const DialogSub = ({ children, ...props }: React.ComponentProps<'div'>) => {
  const [subModalValue, setSubModalValue] = React.useState<string | null>(null);

  const contextValue = React.useMemo<TDialogContext>(
    () => ({
      subModalValue,
      setSubModalValue,
    }),
    [subModalValue, setSubModalValue]
  );
  return (
    <SubDialogContext.Provider value={contextValue}>
      <div
        style={
          {
            '--dialog-border-radius': DIALOG_BORDER_RADIUS,
          } as React.CSSProperties
        }
        {...props}
      >
        {children}
      </div>
    </SubDialogContext.Provider>
  );
};

const DialogSubTrigger = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'> & { value?: string; disabled?: boolean }
>(({ value = '', disabled = false, className, onClick, ...props }, ref) => {
  const { setSubModalValue } = useSubDialog();

  return (
    <div
      ref={ref}
      onClick={(e) => {
        if (disabled) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        setSubModalValue(value);
        onClick?.(e);
      }}
      className={cn(
        'w-full',
        disabled && 'opacity-50 cursor-not-allowed pointer-events-none',
        className
      )}
      aria-disabled={disabled}
      {...props}
    />
  );
});

const DialogSubClose = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'>
>(({ className, ...props }, ref) => {
  const { setSubModalValue } = useSubDialog();

  return (
    <div
      ref={ref}
      onClick={() => setSubModalValue(null)}
      className={cn('', className)}
      {...props}
    />
  );
});

const DialogSubContent = ({
  value = '',
  className,
  size,
  vertical = 'bottom',
  horizontal = 'left',
  ...props
}: React.ComponentProps<'div'> & {
  size?: 'full';
  vertical?: 'center' | 'top' | 'bottom';
  horizontal?: 'left' | 'right';
  value?: string;
}) => {
  const { subModalValue } = useSubDialog();

  if (subModalValue !== value) return;

  return (
    <div
      data-state={subModalValue === value ? 'open' : 'closed'}
      className={cn(
        'absolute duration-200 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 inset-x-0 rounded-xl bg-white text-muted-foreground',
        { 'inset-y-0': size === 'full' },

        { 'top-1/2 -translate-y-1/2': vertical === 'center' },
        { 'top-0': vertical === 'top' },
        { 'bottom-0': vertical === 'bottom' },

        { 'translate-x-[calc(-100%-16px)]': horizontal === 'left' },
        { '-translate-x-[calc(-100%-16px)]': horizontal === 'right' },
        className
      )}
      {...props}
    />
  );
};

const DialogSubHeader = ({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => {
  return (
    <div
      className={cn(
        ' p-6 font-medium rounded-xl w-full flex text-gray-900 items-start self-start justify-between bg-primary-30 sm:text-left',
        className
      )}
      {...props}
    >
      <div className='flex flex-col'>{children}</div>

      <DialogSubClose className='rounded-sm cursor-pointer opacity-70 text-gray-900 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-gray-900'>
        <CloseSquare />
        <span className='sr-only'>Close</span>
      </DialogSubClose>
    </div>
  );
};

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogBody,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  DialogSub,
  DialogSubTrigger,
  DialogSubContent,
  DialogSubHeader,
  DialogSubClose,
};
